import React, { useState, useEffect, useRef } from 'react';
import { CustomerServiceFilled, RiseOutlined, CloseOutlined, CodeFilled } from '@ant-design/icons';

const KAKAO_CHAT_URL = 'https://pf.kakao.com/_xbGxmxdG/chat';
const KAKAO_SERVICE_URL = 'https://open.kakao.com/o/gJXNIbui';

const KakaoFloatButton = ({ bottom = 52 }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth <= 576
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 576);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close popover when clicking outside the button container
    useEffect(() => {
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    const handleOpenChannel = category => {
        const url = category === 'service' ? KAKAO_SERVICE_URL : KAKAO_CHAT_URL;
        window.open(url, '_blank');
        setOpen(false);
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'fixed',
                insetInlineEnd: isMobile ? 16 : 24,
                bottom: isMobile ? 70 : bottom,
                zIndex: 1000,
                fontFamily: 'Pretendard, -apple-system, sans-serif',
            }}
        >
            {/* Premium Original Popover Support Menu */}
            <div
                style={{
                    position: 'absolute',
                    bottom: 56,
                    right: 0,
                    width: isMobile ? 'calc(100vw - 32px)' : '360px',
                    background: '#ffffff',
                    borderRadius: '20px',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    opacity: open ? 1 : 0,
                    transform: open ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.95)',
                    pointerEvents: open ? 'auto' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transformOrigin: 'bottom right',
                }}
            >
                {/* Custom Premium Header */}
                <div
                    style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        padding: '24px 20px',
                        color: '#ffffff',
                        position: 'relative',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                        }}
                    >
                        <div
                            style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(4px)',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CustomerServiceFilled style={{ color: '#818cf8', fontSize: '16px' }} />
                        </div>
                        <span
                            style={{
                                fontSize: '16px',
                                fontWeight: 800,
                                letterSpacing: '-0.3px',
                                color: '#ffffff',
                            }}
                        >
                            ADCARE 원스톱 케어
                        </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', lineHeight: '1.5' }}>
                        기능 제안, 1:1 쇼핑 순위 상승 비결까지 원하시는 상담을 선택해 보세요.
                        선택하시면 소통방으로 이동합니다.
                    </p>
                    <button
                        onClick={() => setOpen(false)}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '16px',
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            fontSize: '16px',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={e => (e.target.style.color = '#ffffff')}
                        onMouseLeave={e => (e.target.style.color = '#94a3b8')}
                    >
                        <CloseOutlined />
                    </button>
                </div>

                {/* Body: Premium Card Inquiries */}
                <div
                    style={{
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    {/* Card A: Premium Marketing Strategy Consultation (Placed on Top) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenChannel('marketing')}
                        onKeyDown={e => e.key === 'Enter' && handleOpenChannel('marketing')}
                        className="adhelp-support-card-marketing"
                        style={{
                            padding: '16px',
                            background: '#fffdf5',
                            border: '1.5px solid #fef08a',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: '#fef9c3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <RiseOutlined style={{ color: '#d97706', fontSize: '20px' }} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    fontSize: '13px',
                                    fontWeight: 800,
                                    color: '#78350f',
                                    marginBottom: '2px',
                                }}
                            >
                                1:1 쇼핑 상위 노출 & 순위 상승 상담
                            </div>
                            <div style={{ fontSize: '11px', color: '#b45309', lineHeight: '1.4' }}>
                                쇼핑 플랫폼별 순위 상승 비결 및 최적화 노출 대행 의뢰
                            </div>
                        </div>
                    </div>

                    {/* Card B: ADHELP Service Support (Placed at the Bottom with Developer Icon) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenChannel('service')}
                        onKeyDown={e => e.key === 'Enter' && handleOpenChannel('service')}
                        className="adhelp-support-card"
                        style={{
                            padding: '16px',
                            background: '#f8fafc',
                            border: '1.5px solid #e2e8f0',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: '#eef2ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <CodeFilled style={{ color: '#4f46e5', fontSize: '20px' }} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    fontSize: '13px',
                                    fontWeight: 800,
                                    color: '#1e293b',
                                    marginBottom: '2px',
                                }}
                            >
                                ADCARE 서비스 문의 & 제안
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                                기능 추가 요청, 사용법 안내 및 오류의 신속한 해결
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer: Operation Hours */}
                <div
                    style={{
                        background: '#f8fafc',
                        padding: '12px',
                        borderTop: '1px solid #f1f5f9',
                        textAlign: 'center',
                        fontSize: '11px',
                        color: '#64748b',
                        fontWeight: 500,
                    }}
                >
                    <span style={{ color: '#10b981', marginRight: '4px' }}>●</span>
                    상담 운영시간 : 평일 10:00 - 18:00 (주말 및 공휴일 제외)
                </div>
            </div>

            {/* Float Trigger Button */}
            <div
                className="kakao-float-wrap"
                role="button"
                tabIndex={0}
                onClick={() => setOpen(!open)}
                onKeyDown={e => e.key === 'Enter' && setOpen(!open)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                {!isMobile && (
                    <div
                        className="kakao-float-label"
                        style={{
                            opacity: open ? 0.5 : 1,
                            transition: 'opacity 0.2s',
                            background: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#334155',
                        }}
                    >
                        {open ? '상담 창 닫기' : '전문가에게 맞춤 전략 상담받기'}
                    </div>
                )}
                <button
                    className="kakao-float-btn"
                    style={{
                        background: open ? '#0f172a' : '#ffeb00',
                        color: open ? '#ffffff' : '#3c1e1e',
                        boxShadow: open
                            ? '0 8px 24px rgba(15, 23, 42, 0.25)'
                            : '0 8px 24px rgba(254, 240, 138, 0.35)',
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        border: 'none',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: 'none',
                    }}
                >
                    {open ? (
                        <CloseOutlined style={{ fontSize: '14px', color: '#ffffff' }} />
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                            <path
                                d="M11 2C5.753 2 1.5 5.36 1.5 9.5c0 2.7 1.62 5.07 4.08 6.48l-.9 3.35c-.08.3.27.55.53.37l4.05-2.72c.57.07 1.15.12 1.74.12C16.247 17.1 20.5 13.64 20.5 9.5S16.247 2 11 2z"
                                fill="#3C1E1E"
                            />
                        </svg>
                    )}
                </button>
            </div>

            {/* Custom Interactive Styles */}
            <style>{`
                .adhelp-support-card:hover {
                    background: #eef2ff !important;
                    border-color: #a5b4fc !important;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px rgba(79, 70, 229, 0.08);
                }
                .adhelp-support-card-marketing:hover {
                    background: #fefce8 !important;
                    border-color: #fde047 !important;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px rgba(217, 119, 6, 0.08);
                }
            `}</style>
        </div>
    );
};

export default KakaoFloatButton;
